import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { isUndefined } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useTransform from "@/hooks/use-transform";
import SendOrderSubmitView from "@/pages/order/components/view/confirm.order.submit.view";
import ReceivedOrderSubmitView from "@/pages/order/components/view/received.order.submit.view";
import { ROLE } from "@/types";
import useOrder from "@/hooks/use-order";
import useRegisterCompany from "@/hooks/use-register-company";
import { SendedProducts } from "./components/sended-products";
import RecievedProducts from "./components/recieved-products";
import ProductsSection from "./components/products";
import { OrdersInvoices } from "./components/orders-invoices";
import PaymentMethods from "./components/payment-methods";
import CompanyCard from "./components/cards/company-card";
import TenderCard from "./components/cards/tender-card";
import ShipementCard from "./components/cards/shipement-card";
import InfoCard from "./components/cards/info-card";
import { Plus, X } from "lucide-react";
import ConfirmDialog from "@/app/global-components/ConfirmDialog";

interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
  deliveryMethod: string;
  discount: number;
  notice: string;
  image: string;
  attachment: string;
  companyId: string;
  isCanceled: boolean;
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}
interface Invoice {
  fileName: string;
  companyId: string;
  createdDate: string;
  userId: string;
  enVersion: string;
  arVersion: string;
}
interface Transformation {
  id: string;
  title: string;
  status: "Pending" | "Accepted" | "Refused";
  buyerId: string;
  sellerId: string;
  userId: string;
  orderId: string;
  totalPrice: number;
  transformRequest: boolean;
  withdrawRequest: boolean;
  createdAt: Date;
}
interface Company {
  id: string;
  nameAr: string;
  nameEn: string;
  address: string;
  region: string;
  city: string;
  CompanyNr: string;
}

interface Tender {
  id: string;
  title: string;
  value: number;
  endDate: string;
  deliverDate: string;
  type: string;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  city: string;
  address: string;
  products: Product[];
  receivedProducts: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  company: Company;
  buyCompany: Company;
  Tender: Tender;
  status: string;
  DeliveryMethod: string;
  paymentMethod: string;
  invoices: Invoice[];
  trackingOrders?: TrackingOrderData[];
}

interface TrackingOrder {
  trackingNumber: string;
  phoneNumber: string;
  notes: string;
}

interface TrackingOrderData {
  trackingNumber: string;
  phoneNumber: string;
  companyId: string;
  sellerId: string;
  createdDate: string;
  notes: string;
  _id: string;
}

const OrderPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    fetchOrderById,
    updateSendedProducts,
    updateRecievedProducts,
    cancelOrder,
    isOrderLoading,
    loading,
    error,
  } = useOrder();
  const { upsertTransform, fetchTransforms } = useTransform();
  const [order, setOrder] = useState<Order | null>(null);
  const { getCompanyMine } = useRegisterCompany();
  const [sendedItems, setSendedItems] = useState<Product[]>([]);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const [isTransformationPopupOpen, setIsTransformationPopupOpen] =
    useState(false);

  const [AvailableCount, setAvailableCount] = useState(0);

  const [isTrackingDialogOpen, setIsTrackingDialogOpen] = useState(false);
  const [trackingOrders, setTrackingOrders] = useState<TrackingOrder[]>([
    { trackingNumber: "", phoneNumber: "", notes: "" },
  ]);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    productId: null as string | null,
    all: false,
  });
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine();
    };
    fetchCompany();
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const response = await fetchTransforms({
          buyerId: myCompany?.id,
          pageSize: 10,
          pageNumber: 0,
          withPaging: false,
        });
        const transforms = response?.data || [];
        const total = transforms.reduce(
          (sum: number, t: Transformation) => sum + t.totalPrice,
          0
        );
        const pending = transforms
          .filter((t: Transformation) => t.status === "Pending")
          .reduce((sum: number, t: Transformation) => sum + t.totalPrice, 0);
        const transformed = transforms
          .filter((t: Transformation) => t.withdrawRequest)
          .reduce((sum: number, t: Transformation) => sum + t.totalPrice, 0);

        setAvailableCount(total - pending - transformed);
      } catch (error) {
        console.error("Error fetching transformations:", error);
      }
    };
    if (myCompany) {
      loadItems();
    }
  }, [myCompany]);

  const fetchOrder = async () => {
    try {
      if (id) {
        const result = await fetchOrderById(id);
        setOrder(result.data);
        setSendedItems([]);
      }
    } catch (err) {
      console.error("Error fetching order:", err);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const calculateTotalPrice = (products: Product[]) =>
    products.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );
  const handleQuantitySChange = (product: Product, newQuantity: number) => {
    const value =
      newQuantity.toString().split("")[0] === "0"
        ? Number(newQuantity.toString().split("")[1])
        : newQuantity;

    setSendedItems((prev) =>
      prev.map((item) =>
        item === product ? { ...item, quantity: value } : item
      )
    );
  };
  const handleQuantityRChange = (product: Product, newQuantity: number) => {
    if (!order) return;
    setOrder((prevOrder) => ({
      ...prevOrder!,
      receivedProducts: prevOrder!.receivedProducts.map((item) =>
        item === product ? { ...item, quantity: newQuantity } : item
      ),
    }));
  };
  const handleSendOrder = async () => {
    setIsTransformationPopupOpen(true);
  };
  const handleConfirmReceived = async () => {
    if (!order) return;

    try {
      const { Tender, company, buyCompany, ...updateOrder } = order;
      const sendedProducts =
        sendedItems.length > 0
          ? [...sendedItems]
          : updateOrder.receivedProducts;
      const updatedOrder = {
        id: order.id,
        receivedProducts: sendedProducts,
      };
      const response = await updateRecievedProducts(updatedOrder).then(
        async () => {
          const result = await fetchOrderById(order.id);
          setOrder(result.data);
          Swal.fire({
            icon: "success",
            title: t("alerts.success.orderConfirmed"),
            showConfirmButton: true,
            confirmButtonText: t("actions.accept"),
            customClass: {
              actions: "swal2-actions-end",
            },
            reverseButtons: locale === "ar",
          });
          setSendedItems([]);
        }
      );
      return response;
    } catch (error) {
      console.error("Error confirming received order:", error);
      return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Planning":
        return "#ff9800";
      case "Opened":
        return "#2196f3";
      case "Accepted":
        return "#4caf50";
      case "Sending":
        return "#9c27b0";
      case "PARTSENDING":
        return "#9c27b0";
      case "Receiving":
        return "#ff5722";
      case "Finished":
        return "#4caf50";
      case "Revision":
        return "#f44336";
      case "Canceled":
        return "#f44336";
      default:
        return "#757575";
    }
  };
  const getStatusLabel = (status: string) => {
    const statuses = [
      { label: t("status.planning"), name: "Planning" },
      { label: t("general.purchaseRequest"), name: "Opened" },
      { label: t("status.accepted"), name: "Accepted" },
      { label: t("status.shipping"), name: "Sending" },
      { label: t("status.shipping"), name: "PARTSENDING" },
      { label: t("status.received"), name: "Receiving" },
      { label: t("status.completed"), name: "Finished" },
      { label: t("general.reviewRequest"), name: "Revision" },
      { label: t("status.rejected"), name: "Canceled" },
    ];
    const statusItem = statuses.find((s) => s.name === status);
    return statusItem ? statusItem.label : t("status.unknown");
  };
  const handleAddToSended = (product: Product) => {
    if (!order) return;
    setSendedItems((prev) => [...prev, { ...product }]);
  };
  const handleRemoveFromSended = (product: Product) => {
    setSendedItems((prev) =>
      prev.filter(
        (item) =>
          item.itemId !== product.itemId ||
          order?.Sendedproducts.some((p) => p === product)
      )
    );
  };

  // Open dialog for single product
  const handleOpenCancelDialog = (productId: string) => {
    setConfirmDialog({ open: true, productId, all: false });
  };
  // Open dialog for all products
  const handleOpenCancelAllDialog = () => {
    setConfirmDialog({ open: true, productId: null, all: true });
  };
  // Close dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, productId: null, all: false });
  };
  // Confirm action
  const handleConfirmCancel = async () => {
    setConfirmLoading(true);
    if (confirmDialog.all) {
      await handleCancelAllSellerProducts(true); // skipDialog = true
    } else if (confirmDialog.productId) {
      await handleCancelProduct(confirmDialog.productId, true); // skipDialog = true
    }
    setConfirmLoading(false);
    setConfirmDialog({ open: false, productId: null, all: false });
  };

  // Cancel a single product (with dialog logic)
  const handleCancelProduct = async (productId: string, skipDialog = false) => {
    if (!order) return;
    if (!skipDialog) {
      handleOpenCancelDialog(productId);
      return;
    }
    try {
      const response = await cancelOrder(order.id, [productId]);
      if (response.success) {
        await fetchOrder();
        // Optionally show a MUI Snackbar for success
      } else {
        // Optionally show a MUI Snackbar for error
      }
    } catch (error) {
      // Optionally show a MUI Snackbar for error
    }
  };
  // Cancel all seller products (with dialog logic)
  const handleCancelAllSellerProducts = async (skipDialog = false) => {
    if (!order) return;
    if (!skipDialog) {
      handleOpenCancelAllDialog();
      return;
    }
    try {
      const sellerProductIds = order.products
        .filter((product) => product.companyId === myCompany?.id)
        .map((product) => product.itemId);
      if (sellerProductIds.length === 0) {
        // Optionally show a MUI Snackbar for info
        return;
      }
      const response = await cancelOrder(order.id, sellerProductIds);
      if (response.success) {
        await fetchOrder();
        // Optionally show a MUI Snackbar for success
      } else {
        // Optionally show a MUI Snackbar for error
      }
    } catch (error) {
      // Optionally show a MUI Snackbar for error
    }
  };

  const handleAddTrackingOrder = () => {
    setTrackingOrders((prev) => [
      ...prev,
      { trackingNumber: "", phoneNumber: "", notes: "" },
    ]);
  };

  const handleRemoveTrackingOrder = (index: number) => {
    if (trackingOrders.length > 1) {
      setTrackingOrders((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleUpdateTrackingOrder = (
    index: number,
    field: keyof TrackingOrder,
    value: string
  ) => {
    setTrackingOrders((prev) =>
      prev.map((order, i) =>
        i === index ? { ...order, [field]: value } : order
      )
    );
  };

  const handleOpenTrackingDialog = () => {
    setTrackingOrders([{ trackingNumber: "", phoneNumber: "", notes: "" }]);
    setIsTrackingDialogOpen(true);
  };

  const handleShipment = async () => {
    if (!order) return;
    const { Tender, company, buyCompany, ...updateOrder } = order;
    const sendedProducts =
      sendedItems.length > 0 ? [...sendedItems] : updateOrder.Sendedproducts;
    try {
      const updatedOrder = {
        id: order.id,
        Sendedproducts: sendedProducts,
        trackingOrders: trackingOrders,
      };
      const response = await updateSendedProducts(updatedOrder);
      setSendedItems([]);
      setIsTrackingDialogOpen(false);
      fetchOrder();
      return response;
    } catch (error) {
      console.error("Error sending order with transformation:", error);
      return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {t("alerts.error.fetchOrderDetails")}
        </Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          {t("alerts.error.noOrderDetails")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: "100%",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: { xs: "1.75rem", md: "2.125rem" },
            }}
          >
            {t("orders.details")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Order #{order.OrderNr} • {order.title}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Chip
            label={getStatusLabel(order.status)}
            sx={{
              backgroundColor: getStatusColor(order.status),
              color: "white",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 2,
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Order Info Card */}
        <Grid item xs={12} md={6} lg={3}>
          <InfoCard order={order} calculateTotalPrice={calculateTotalPrice} />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <CompanyCard order={order} />
        </Grid>

        {order.Tender && (
          <Grid item xs={12} md={6} lg={3}>
            <TenderCard order={order} />
          </Grid>
        )}
        <Grid item xs={12} md={6} lg={3}>
          <ShipementCard order={order} />
        </Grid>
      </Grid>

      {userRole === ROLE.BUYER && order.status === "Planning" && (
        <PaymentMethods
          setIsTransformationPopupOpen={setIsTransformationPopupOpen}
          isTransformationPopupOpen={isTransformationPopupOpen}
          calculateTotalPrice={calculateTotalPrice}
          upsertTransform={upsertTransform}
          order={order}
          setOrder={setOrder}
          AvailableCount={AvailableCount}
        />
      )}

      {/* Tracking Orders Section */}
      {order.trackingOrders && order.trackingOrders.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "none",
              border: "1px solid #e5e7eb",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  {t("orders.trackingInformation")}
                </Typography>
                <Chip
                  label={`${order.trackingOrders.length} ${
                    order.trackingOrders.length === 1
                      ? t("orders.trackingOrder")
                      : t("orders.trackingOrders")
                  }`}
                  sx={{
                    backgroundColor: "#18181b",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                {order.trackingOrders.map((trackingOrder, index) => (
                  <Grid item xs={12} key={trackingOrder._id || index}>
                    <Card
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6b7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {t("orders.trackingNumber")}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#374151",
                                  mt: 0.5,
                                }}
                              >
                                {trackingOrder.trackingNumber}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6b7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {t("orders.phoneNumber")}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#374151",
                                  mt: 0.5,
                                }}
                              >
                                {trackingOrder.phoneNumber}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6b7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {t("orders.createdDate")}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#374151",
                                  mt: 0.5,
                                }}
                              >
                                {new Date(
                                  trackingOrder.createdDate
                                ).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6b7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {t("orders.fields.notes")}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#374151",
                                  mt: 0.5,
                                  fontStyle: trackingOrder.notes
                                    ? "normal"
                                    : "italic",
                                }}
                              >
                                {trackingOrder.notes || t("orders.noNotes")}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      <ProductsSection
        isMobile={isMobile}
        order={order}
        sendedItems={sendedItems}
        handleAddToSended={handleAddToSended}
        handleRemoveFromSended={handleRemoveFromSended}
        onCancelProduct={handleCancelProduct}
      />

      {userRole === ROLE.SELLER &&
        (order.Sendedproducts.length > 0 || sendedItems.length > 0) && (
          <SendedProducts
            handleQuantitySChange={handleQuantitySChange}
            sendedItems={sendedItems}
            handleRemoveFromSended={handleRemoveFromSended}
            isMobile={isMobile}
            order={order}
          />
        )}

      {/* Received Products Section */}
      {userRole === ROLE.BUYER &&
        (order.receivedProducts.length > 0 || sendedItems.length > 0) && (
          <RecievedProducts
            handleQuantitySChange={handleQuantitySChange}
            handleQuantityRChange={handleQuantityRChange}
            sendedItems={sendedItems}
            handleRemoveFromSended={handleRemoveFromSended}
            isMobile={isMobile}
            order={order}
          />
        )}

      {order.invoices && order.invoices.length > 0 && (
        <OrdersInvoices order={order} isMobile={isMobile} />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 4,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        {order.status === "Planning" && userRole === ROLE.BUYER && (
          <SendOrderSubmitView
            isLoading={isOrderLoading}
            onClick={handleSendOrder}
          />
        )}

        {/* Cancel All roducts button for sellers */}
        {userRole === ROLE.SELLER &&
          order.status !== "finished" &&
          order.products.some(
            (product) => product.companyId === myCompany?.id
          ) && (
            <Button
              onClick={handleOpenCancelAllDialog}
              variant="outlined"
              color="error"
              sx={{
                borderColor: "#ef4444",
                color: "#ef4444",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#dc2626",
                  backgroundColor: "#fef2f2",
                },
              }}
            >
              {t("actions.cancel")}{" "}
              {t("buyer_pages.return_request_details.order")}
            </Button>
          )}

        {userRole === ROLE.SELLER &&
          (order.status === "Accepted" || order.status === "PARTSENDING") &&
          !!sendedItems.length && (
            <Button
              onClick={handleOpenTrackingDialog}
              variant="contained"
              sx={{
                backgroundColor: "#18181b",
                color: "white",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              {t("orders.sendShipment")}
            </Button>
          )}

        {userRole === ROLE.BUYER &&
          sendedItems.length > 0 &&
          (order.status === "Sending" || order.status === "PARTSENDING") && (
            <ReceivedOrderSubmitView
              isLoading={loading}
              onClick={() => {
                handleConfirmReceived();
              }}
            />
          )}
      </Box>

      {/* Tracking Orders Dialog */}
      <Dialog
        open={isTrackingDialogOpen}
        onClose={() => setIsTrackingDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
            m: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
            borderBottom: "1px solid #e5e7eb",
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#374151" }}>
              {t("orders.trackingInformation")}
            </Typography>
            <IconButton
              onClick={() => setIsTrackingDialogOpen(false)}
              sx={{
                color: "#6b7280",
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                },
              }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("orders.trackingDescription")}
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("orders.trackingNumber")} *
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("orders.phoneNumber")} *
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("orders.fields.notes")}
                  </TableCell>
                  <TableCell sx={{ width: 60 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trackingOrders.map((trackingOrder, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={trackingOrder.trackingNumber}
                        onChange={(e) =>
                          handleUpdateTrackingOrder(
                            index,
                            "trackingNumber",
                            e.target.value
                          )
                        }
                        placeholder={t("orders.enterTrackingNumber")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={trackingOrder.phoneNumber}
                        onChange={(e) =>
                          handleUpdateTrackingOrder(
                            index,
                            "phoneNumber",
                            e.target.value
                          )
                        }
                        placeholder={t("orders.enterPhoneNumber")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={trackingOrder.notes}
                        onChange={(e) =>
                          handleUpdateTrackingOrder(
                            index,
                            "notes",
                            e.target.value
                          )
                        }
                        placeholder={t("orders.enterNotes")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {trackingOrders.length > 1 && (
                        <IconButton
                          onClick={() => handleRemoveTrackingOrder(index)}
                          size="small"
                          sx={{
                            color: "#ef4444",
                            "&:hover": {
                              backgroundColor: "#fef2f2",
                            },
                          }}
                        >
                          <X size={16} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            onClick={handleAddTrackingOrder}
            startIcon={<Plus size={16} />}
            variant="outlined"
            sx={{
              borderColor: "#d1d5db",
              color: "#6b7280",
              borderRadius: 2,
              "&:hover": {
                borderColor: "#644980",
                color: "#644980",
              },
            }}
          >
            {t("orders.addTrackingOrder")}
          </Button>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
            borderTop: "1px solid #e5e7eb",
            gap: 2,
          }}
        >
          <Button
            onClick={() => setIsTrackingDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#d1d5db",
              color: "#6b7280",
              borderRadius: 2,
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            onClick={handleShipment}
            variant="contained"
            disabled={trackingOrders.some(
              (order) => !order.trackingNumber || !order.phoneNumber
            )}
            sx={{
              backgroundColor: "#18181b",
              color: "white",
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#000000",
              },
              "&:disabled": {
                backgroundColor: "#d1d5db",
                color: "#9ca3af",
              },
            }}
          >
            {t("orders.sendShipment")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ConfirmDialog for cancel actions */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={t("actions.cancel")}
        content={
          confirmDialog.all
            ? t("orders.cancelAllConfirm")
            : t("alerts.confirmation.deleteItem")
        }
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmCancel}
        confirmText={t("actions.accept")}
        cancelText={t("actions.cancel")}
        loading={confirmLoading}
        dir={locale === "ar" ? "rtl" : "ltr"}
      />
    </Box>
  );
};

export default OrderPage;
